import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { startExportJob, getExportJob, cancelExportJob, downloadExport, exportFilename, errorText, repaintAndWait } from "../lib/api";

const IDLE = { open: false, phase: "preparing", percent: 0, filename: "", fmt: "" };
const POLL_MS = 1500;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Drives the download-progress modal via the background export job:
// preparing (server builds the mesh, we poll) → downloading (real 0→100% transfer).
// Cancel aborts polling/transfer and tells the server to drop a still-queued job.
// A 3MF whose previous file expired (24 h cache) pauses in an "expired" phase so the maker can Repaint & preview first.
// `onUpdate` receives the repainted creation so the page can show the refreshed turntable and preview.
export const useDownload = ({ onUpdate } = {}) => {
  const [state, setState] = useState(IDLE);
  const ctx = useRef({ abort: null, creation: null, jobId: null, cancelled: false });
  const pending = useRef(null);

  // On unmount: stop the polling loop and abort any in-flight transfer (prevents setState-after-unmount + leaks).
  useEffect(() => () => {
    ctx.current.cancelled = true;
    ctx.current.abort?.abort();
  }, []);

  const startDownload = useCallback(async (creation, fmt = "3mf", height = 70, colors = 8, force = false, qr = null) => {
    const abort = new AbortController();
    ctx.current = { abort, creation, jobId: null, cancelled: false };
    setState({ open: true, phase: "preparing", percent: 0, filename: exportFilename(creation, fmt), fmt });
    try {
      const started = await startExportJob(creation, fmt, height, colors, abort.signal, force, qr);
      if (started.status === "expired") {
        pending.current = { creation, fmt, height, colors, qr };
        setState((s) => ({ ...s, phase: "expired", previousAt: started.previous_at }));
        return;
      }
      if (started.status !== "done") {
        ctx.current.jobId = started.job_id;
        // Poll until the background job finishes building the file.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await sleep(POLL_MS);
          if (ctx.current.cancelled) return;
          if (typeof document !== "undefined" && document.hidden) continue;  // pause polling while the tab is hidden
          const st = await getExportJob(creation, started.job_id, abort.signal);
          if (st.status === "done") break;
          if (st.status === "cancelled") { setState(IDLE); return; }  // cancelled elsewhere (e.g. another tab) — close the modal
          if (st.status === "error") throw new Error(st.error || "Export failed");
        }
      }
      if (ctx.current.cancelled) return;
      // File is now warm in the cache — pull it with a real transfer progress bar.
      await downloadExport(creation, fmt, height, colors, ({ phase, percent }) => setState((s) => ({ ...s, phase, percent })), abort.signal, qr);
      setState((s) => ({ ...s, phase: "downloading", percent: 100 }));
      if (fmt === "3mf") {  // a 3MF may have been written with a "minor automatic repair" — refresh so the badge shows it
        try {
          const { data } = await api.get(`/creations/${creation.id}`);
          onUpdate?.(data);
        } catch { /* non-fatal: the file already downloaded */ }
      }
      setTimeout(() => setState(IDLE), 700);
    } catch (e) {
      if (ctx.current.cancelled || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      setState(IDLE);
      toast.error(e?.message?.startsWith("Export") ? e.message : "Download failed — please try again.");
    }
  }, []);

  const downloadAnyway = useCallback(() => {
    const p = pending.current;
    if (p) startDownload(p.creation, p.fmt, p.height, p.colors, true, p.qr);
  }, [startDownload]);

  const repaintAndDownload = useCallback(async () => {
    const p = pending.current;
    if (!p) return;
    setState((s) => ({ ...s, phase: "repainting" }));
    try {
      const data = await repaintAndWait(p.creation.id); // starts the async repaint then polls to completion
      onUpdate?.(data);
      toast.success("Colours and preview refreshed");
      startDownload(data || p.creation, p.fmt, p.height, p.colors, true, p.qr);
    } catch (e) {
      setState(IDLE);
      toast.error(e?.message || errorText(e, "Could not repaint this duck — please try again."));
    }
  }, [onUpdate, startDownload]);

  const cancelDownload = useCallback(() => {
    const { abort, creation, jobId } = ctx.current;
    ctx.current.cancelled = true;
    abort?.abort();
    if (creation && jobId) cancelExportJob(creation, jobId); // drop a still-queued server job
    setState(IDLE);
  }, []);

  return { downloadState: state, startDownload, cancelDownload, repaintAndDownload, downloadAnyway };
};
